import { useEffect, useState } from 'react';
import { Alert, Button, Snackbar, Typography } from '@mui/material';
import { GetApp } from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'install-prompt-dismissed';

const isIos = () => /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    if (isIos()) {
      const timer = setTimeout(() => {
        setShowIosTip(true);
        setOpen(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    const handler = (e: Event) => {
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setOpen(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setOpen(false);
  };

  const handleDismiss = () => {
    setOpen(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  if (!open) return null;

  if (showIosTip) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" onClose={handleDismiss} icon={<GetApp />} sx={{ width: '100%' }}>
          <Typography variant="body2">
            Nainstaluj si aplikaci: klikni na <strong>Sdílet</strong> a pak <strong>Přidat na plochu</strong>.
          </Typography>
        </Alert>
      </Snackbar>
    );
  }

  if (deferredPrompt) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="info"
          onClose={handleDismiss}
          icon={<GetApp />}
          action={
            <Button color="inherit" size="small" onClick={handleInstall}>
              Nainstalovat
            </Button>
          }
          sx={{ width: '100%' }}
        >
          Nainstaluj si MenuMaker jako aplikaci
        </Alert>
      </Snackbar>
    );
  }

  return null;
};

export default InstallPrompt;
